import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';

import cx from 'classnames';

import { ImageKitContext, WidgetsContext } from './../core/context';
import { useWidgetSelectorForType } from './../core/hooks';
import BackgroundView from '../background';
import Portal from '../../../components/Portal';
import { CarouselContext } from '../InteractiveContent';
import { HotspotRender } from '../../../components/hotspot';

import { HOTSPOT } from './../../../constants/widgetTypes';
import { AnimatePresence, motion } from 'framer-motion'

import { INTERACTIVE_CONTENT_TYPES, SEGMENT_STORYBOARDS, STORYBOARD_TYPES } from './../../../constants/storyboardTypes';
import { isIcV2, useInteractiveContent } from '../InteractiveContent/v2';
import styles from './page.module.scss';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';


export const PageContext = React.createContext({
  zoom: 1,
});

const PageWidgetComponent = ({ className, style, widgetProps, breakpoint, children, storyboard, ...rest }) => {
  const id = get(widgetProps, 'uid');
  const pageRef = useRef(null);
  const container = get(widgetProps, `properties.${breakpoint}.container`);
  const background = get(widgetProps, 'properties.desktop.background');
  const layout = get(widgetProps, `properties.${breakpoint}.layout`, {});
  const layoutWidth = parseInt(get(layout, 'width', '1200'));
  const context = React.useContext(WidgetsContext);
  const imageKitCDN = get(rest, 'nextjsRendering.urlEndpoint', "");
  const carouselContext = React.useContext(CarouselContext);
  const { tabletScreens = false, extraSmallScreens } = useInteractiveContent()
  const isSmallScreen = tabletScreens || extraSmallScreens;
  const isMobile = breakpoint === 'mobile';

  const [zoom, setZoom] = useState(1);


  const storyboardType = get(context, 'storyboard.type', '');
  const activePage = get(carouselContext, 'activePage');

  useEffect(() => {
    calculateZoom();
    window.addEventListener('resize', calculateZoom);
    return () => {
      window.removeEventListener('resize', calculateZoom);
    }
  }, [storyboardType, breakpoint, layoutWidth]);

  const calculateZoom = () => {
    const windowWidth = document.body.clientWidth;
    var zoomValue = 1;
    if (context.shouldZoom) {
      zoomValue = windowWidth / container.minWidth;

      if (!INTERACTIVE_CONTENT_TYPES.includes(storyboardType) && windowWidth > 1200) {
        zoomValue = 1;
      }
    }

    setZoom(zoomValue);
  }

  var widgetStyle = {
    ...style,
    width: '100%',
    minWidth: container.minWidth,
  }

  if (breakpoint === 'mobile') {
    var widgetStyle = {
      ...style,
      width: container.minWidth,
      height: layout.height
    }
  }

  const pageOrder = get(widgetProps, 'order', 1);
  const seamStep = (SEGMENT_STORYBOARDS.includes(storyboardType) && pageOrder > 1) ? 1 : 0;
  
  widgetStyle = {
    ...widgetStyle,
    position: 'absolute',
    transform: `scale(${zoom})`,
    marginBottom: SEGMENT_STORYBOARDS.includes(storyboardType) ? `calc(${layout.height} * (${zoom} - 1) - ${seamStep}px)` : `0px`,
    ...(isIcV2(storyboardType) && isMobile) ? { marginInline: extraSmallScreens ? 0 : 'auto' } : {}, // To fix the page aligment in the mobile view
  }

  const pageContextValue = React.useMemo(() => ({
    zoom: zoom,
    pageWidth: container.minWidth
  }), [zoom, container.minWidth]);

  return (
    <ImageKitContext.Provider value={imageKitCDN}>
      <PageContext.Provider value={pageContextValue}>
        <div id={id}
          className={cx(className, styles.page, 'cs_page_root', {
            [styles.mobile]: isMobile,
            [styles.landingPage]: !INTERACTIVE_CONTENT_TYPES.includes(storyboardType),
          [styles.smallScreen]: isSmallScreen,
          })}
          ref={pageRef}
          data-widget-id={id}
          data-container={true}
          style={widgetStyle}
          {...rest}
      >
          <PageBackground id={id} breakpoint={breakpoint} context={context} ref={pageRef} storyboardType={storyboardType} background={background} activePage={activePage} />
          <HotspotRender breakpoint={breakpoint}>
            {children}
          </HotspotRender>
        </div>
      </PageContext.Provider>
    </ImageKitContext.Provider>
  );
}

const PageBackground = React.forwardRef(({ id, breakpoint, context, background, storyboardType, activePage }, ref) => {
  if (!background || context.development) {
    return null;
  }

  if ([STORYBOARD_TYPES.INTERACTIVE_CONTENT].includes(storyboardType)) {

    const activeId = ref.current ? ref.current.parentElement.id : null;
    const activePageElement = ref.current ? ref.current.parentElement : null;
    const activePageParent = activePageElement ? activePageElement.parentElement : null;

    const { index, fullscreen } = activePage;

    useLayoutEffect(() => {
      const parentPage = document.getElementById(`page_${index}`)
      if (fullscreen && (activeId == index) && parentPage) {
        parentPage.style.backgroundColor = 'transparent';
      }
    }, [fullscreen, index])

    return (
      <React.Fragment key={id}>
        <AnimatePresence>
          {(fullscreen && activeId == index) && breakpoint === 'desktop' && (
            <motion.div>
              <Portal>
                <BackgroundView
                  className={styles.pageBackground}
                  background={background}
                  breakpoint={breakpoint}
                  isIcPageBackground={true}
                />
              </Portal>
            </motion.div>
          )}
        </AnimatePresence>
        {(fullscreen &&
          activeId == index) &&
          (breakpoint === 'mobile' &&
          activePageParent) && (
            <Portal mountNode={activePageParent}>
              <BackgroundView
                className={styles.pageBackgroundMobile}
                background={background}
                breakpoint={breakpoint}
                isIcPageBackground={true}
              />
            </Portal>
          )}
        <AnimatePresence>
          {!fullscreen && (
            <motion.div>
              <BackgroundView
                className={styles.pageBackground}
                background={background}
                breakpoint={breakpoint}
                isIcPageBackground={true}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </React.Fragment>
    )
  }

  return (
      <BackgroundView
        className={cx(styles.landingPageBackground, {[styles.mobileLandingPageBackground]: breakpoint === 'mobile'})}
        background={background}
        breakpoint={breakpoint}
      />
  )
})

const pageWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'breakpoint', 'children'];
  const next = pick(nextProps, paths);
  const previous = pick(prevProps, paths);

  const equals = isEqual(next, previous);
  return equals;
}

export const PageWidget = React.memo(PageWidgetComponent, pageWidgetEquals);

